import React, { useState } from 'react';
import { Mail, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';

const ForgotPassword = ({ onBack, apiEndpoint }) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess(false);

    try {
      const response = await fetch(`${apiEndpoint}/request-password-reset`, {

        //const response = await fetch(`${apiEndpoint}/go-request-password-reset-lambda`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email.toLowerCase().trim() }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        setEmail('');
      } else {
        setError(data.error || 'Failed to send reset email');
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const styles = {
    container: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    card: {
      backgroundColor: 'white',
      borderRadius: '16px',
      boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
      padding: '40px',
      width: '100%',
      maxWidth: '500px'
    },
    backButton: {
      display: 'flex',
      alignItems: 'center',
      color: '#6b7280',
      backgroundColor: 'transparent',
      border: 'none',
      fontSize: '14px',
      cursor: 'pointer',
      marginBottom: '24px',
      padding: '8px 0',
      transition: 'color 0.3s'
    },
    iconWrapper: {
      display: 'inline-block',
      padding: '12px',
      backgroundColor: '#ede9fe',
      borderRadius: '50%',
      marginBottom: '16px'
    },
    heading: {
      fontSize: '28px',
      fontWeight: 'bold',
      color: '#111827',
      margin: '0 0 8px 0'
    },
    subheading: {
      color: '#6b7280',
      marginTop: '8px',
      fontSize: '14px'
    },
    successBox: {
      backgroundColor: '#f0fdf4',
      border: '1px solid #bbf7d0',
      borderRadius: '12px',
      padding: '16px',
      marginBottom: '24px',
      display: 'flex',
      alignItems: 'flex-start'
    },
    errorBox: {
      backgroundColor: '#fef2f2',
      border: '1px solid #fecaca',
      borderRadius: '12px',
      padding: '16px',
      marginBottom: '24px',
      display: 'flex',
      alignItems: 'flex-start'
    },
    label: {
      display: 'block',
      color: '#374151',
      fontWeight: '600',
      marginBottom: '8px',
      fontSize: '14px'
    },
    inputWrapper: {
      position: 'relative',
      marginBottom: '24px'
    },
    input: {
      width: '100%',
      padding: '12px 12px 12px 40px',
      border: '2px solid #e5e7eb',
      borderRadius: '12px',
      fontSize: '16px',
      transition: 'border-color 0.3s',
      boxSizing: 'border-box',
      outline: 'none'
    },
    icon: {
      position: 'absolute',
      left: '12px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#9ca3af'
    },
    primaryButton: {
      width: '100%',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '12px',
      padding: '16px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s',
      boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)'
    },
    secondaryButton: {
      color: '#667eea',
      backgroundColor: 'transparent',
      border: 'none',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer'
    },
    spinner: {
      display: 'inline-block',
      width: '20px',
      height: '20px',
      border: '3px solid rgba(255,255,255,0.3)',
      borderTop: '3px solid white',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
      marginRight: '8px'
    }
  };

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .hover-button:hover {
          background: linear-gradient(135deg, #5568d3 0%, #63408b 100%);
          box-shadow: 0 6px 20px rgba(102, 126, 234, 0.5);
        }
        .input-focus:focus {
          border-color: #667eea;
        }
      `}</style>
      
      <div style={styles.card}>
        <button
          onClick={onBack}
          style={styles.backButton}
          onMouseOver={(e) => e.target.style.color = '#111827'}
          onMouseOut={(e) => e.target.style.color = '#6b7280'}
        >
          <ArrowLeft size={20} style={{ marginRight: '8px' }} />
          Back to Login
        </button>

        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={styles.iconWrapper}>
            <Mail size={32} style={{ color: '#667eea' }} />
          </div>
          <h2 style={styles.heading}>Forgot Password?</h2>
          <p style={styles.subheading}>
            No worries! Enter your email and we'll send you reset instructions.
          </p>
        </div>

        {success ? (
          <div style={styles.successBox}>
            <CheckCircle style={{ color: '#10b981', marginTop: '2px', marginRight: '12px', flexShrink: 0 }} size={20} />
            <div>
              <h3 style={{ fontWeight: '600', color: '#065f46', marginBottom: '4px', marginTop: 0 }}>Email Sent!</h3>
              <p style={{ color: '#047857', fontSize: '14px', margin: 0 }}>
                If an account with that email exists, you'll receive password reset instructions shortly.
                Check your spam folder if you don't see it.
              </p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {error && (
              <div style={styles.errorBox}>
                <AlertCircle style={{ color: '#ef4444', marginTop: '2px', marginRight: '12px', flexShrink: 0 }} size={20} />
                <p style={{ color: '#dc2626', fontSize: '14px', margin: 0 }}>{error}</p>
              </div>
            )}

            <div style={{ marginBottom: '24px' }}>
              <label style={styles.label}>Email Address</label>
              <div style={styles.inputWrapper}>
                <Mail style={styles.icon} size={20} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  className="input-focus"
                  style={styles.input}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading || !email}
              className="hover-button"
              style={{
                ...styles.primaryButton,
                opacity: (isLoading || !email) ? 0.5 : 1,
                cursor: (isLoading || !email) ? 'not-allowed' : 'pointer'
              }}
            >
              {isLoading ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={styles.spinner}></span>
                  Sending...
                </span>
              ) : (
                'Send Reset Link'
              )}
            </button>
          </form>
        )}

        <div style={{ marginTop: '24px', textAlign: 'center' }}>
          <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
            Remember your password?{' '}
            <button
              onClick={onBack}
              style={styles.secondaryButton}
              onMouseOver={(e) => e.target.style.color = '#5568d3'}
              onMouseOut={(e) => e.target.style.color = '#667eea'}
            >
              Sign In
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
